/**
 * Puddles — fishing holes on the ice. Each renders as a dark ellipse with a rim highlight
 * and idle ripples every few seconds. Exposes hit-test + normalized-coord projection.
 *
 * Used by: background canvas (to draw), fishingGame (to hit test + get centers).
 */
import { PUDDLES, type PuddleConfig } from '../config'

interface Ripple {
  progress: number // 0..1
  delay: number    // seconds remaining before start
}

export interface PuddleRuntime {
  readonly config: PuddleConfig
  cx: number
  cy: number
  rx: number
  ry: number
  ripples: Ripple[]
  nextRippleIn: number
}

export class Puddles {
  private runtimes: PuddleRuntime[] = []

  constructor() {
    this.runtimes = PUDDLES.map((cfg) => ({
      config: cfg,
      cx: 0,
      cy: 0,
      rx: cfg.rx,
      ry: cfg.ry,
      ripples: [],
      nextRippleIn: 2 + Math.random() * 4,
    }))
  }

  resize(w: number, h: number): void {
    for (const p of this.runtimes) {
      p.cx = p.config.nx * w
      p.cy = p.config.ny * h
    }
  }

  getAll(): PuddleRuntime[] {
    return this.runtimes
  }

  /** True if point (x,y) is inside any puddle ellipse. */
  hitTest(x: number, y: number): PuddleRuntime | null {
    for (const p of this.runtimes) {
      const dx = (x - p.cx) / p.rx
      const dy = (y - p.cy) / p.ry
      if (dx * dx + dy * dy <= 1) return p
    }
    return null
  }

  /** Trigger a ripple visually (e.g. when line hits water). */
  spawnRipple(p: PuddleRuntime): void {
    if (p.ripples.length < 3) {
      p.ripples.push({ progress: 0, delay: 0 })
    }
  }

  tick(dt: number): void {
    const stepSec = dt / 1000
    for (const p of this.runtimes) {
      p.nextRippleIn -= stepSec
      if (p.nextRippleIn <= 0) {
        this.spawnRipple(p)
        p.nextRippleIn = 3 + Math.random() * 4
      }
      for (const r of p.ripples) {
        if (r.delay > 0) {
          r.delay -= stepSec
          continue
        }
        r.progress += stepSec * 0.8
      }
      p.ripples = p.ripples.filter((r) => r.progress < 1)
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const p of this.runtimes) {
      // Outer soft rim — simulates a thin dark edge where ice meets water
      ctx.save()
      ctx.fillStyle = 'rgba(0, 42, 92, 0.18)'
      ctx.beginPath()
      ctx.ellipse(p.cx, p.cy + 3, p.rx + 6, p.ry + 5, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()

      // Water body — teal-to-deep gradient (light theme)
      ctx.save()
      const g = ctx.createRadialGradient(p.cx, p.cy - p.ry * 0.2, 2, p.cx, p.cy, p.rx)
      g.addColorStop(0, '#8bd4f0')
      g.addColorStop(0.5, '#2a9dc6')
      g.addColorStop(1, '#004a7a')
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.ellipse(p.cx, p.cy, p.rx, p.ry, 0, 0, Math.PI * 2)
      ctx.fill()

      // Rim highlight
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)'
      ctx.lineWidth = 1.6
      ctx.stroke()

      // Inner cyan glow ring
      ctx.strokeStyle = 'rgba(0, 242, 254, 0.55)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.ellipse(p.cx, p.cy, p.rx - 3, p.ry - 2, 0, 0, Math.PI * 2)
      ctx.stroke()

      // Specular highlight (upper-left)
      const spec = ctx.createRadialGradient(
        p.cx - p.rx * 0.25, p.cy - p.ry * 0.45, 0,
        p.cx - p.rx * 0.25, p.cy - p.ry * 0.45, p.rx * 0.5,
      )
      spec.addColorStop(0, 'rgba(255, 255, 255, 0.55)')
      spec.addColorStop(1, 'rgba(255, 255, 255, 0)')
      ctx.fillStyle = spec
      ctx.beginPath()
      ctx.ellipse(p.cx - p.rx * 0.25, p.cy - p.ry * 0.45, p.rx * 0.55, p.ry * 0.25, 0, 0, Math.PI * 2)
      ctx.fill()

      ctx.restore()

      // Ripples
      for (const r of p.ripples) {
        if (r.delay > 0) continue
        ctx.save()
        const eased = 1 - Math.pow(1 - r.progress, 2)
        ctx.strokeStyle = `rgba(255, 255, 255, ${(1 - r.progress) * 0.75})`
        ctx.lineWidth = 1.4
        ctx.beginPath()
        ctx.ellipse(
          p.cx,
          p.cy,
          p.rx * (0.25 + eased * 0.9),
          p.ry * (0.25 + eased * 0.9),
          0,
          0,
          Math.PI * 2,
        )
        ctx.stroke()
        ctx.restore()
      }
    }
  }

  /** Save/restore + clip to a specific puddle path. Used for drawing fish inside. */
  clipTo(ctx: CanvasRenderingContext2D, p: PuddleRuntime, drawFn: () => void): void {
    ctx.save()
    ctx.beginPath()
    ctx.ellipse(p.cx, p.cy, p.rx, p.ry, 0, 0, Math.PI * 2)
    ctx.clip()
    drawFn()
    ctx.restore()
  }
}
